import { consola } from 'consola'
import { useApi } from '@shared/api'

const logger = consola.withTag('api-alfabank')

export interface AlfaBankSyncStatusResult {
  success: boolean
  /** Заказ оплачен по данным шлюза Альфа-Банка. */
  paid: boolean
  /** Сырой orderStatus шлюза (0–8). */
  orderStatus?: string
  /** Доменный статус платежа. */
  paymentStatus: string
  /** Оплата была достроена по данным шлюза — значит callback потерялся. */
  recovered: boolean
}

/**
 * Конфигурация нативной кнопки Alfa Pay для мобильного приложения.
 *
 * `available: false` — кнопку показывать нельзя (нет открытого токена, тестовый режим
 * заведения и т.п.), приложение должно уйти на обычную платёжную страницу банка.
 */
export interface AlfaPayNativeConfigResult {
  success: boolean
  available: boolean
  reason?: string
  token?: string
  amount?: number
  orderNumber?: string
  returnUrl?: string
  failUrl?: string
  domain?: string
  description?: string
  email?: string
  phone?: string
  clientId?: string
}

/**
 * Клиент оплаты через Альфа-Банк.
 *
 * Ходит ТОЛЬКО в наш бэкенд (`/api/v2/payments/alfabank/*`). Обращаться из браузера
 * напрямую к API банка нельзя: там технические логин/пароль мерчанта, а карточные данные
 * вводятся исключительно на платёжной странице банка.
 */
class APIAlfaBank {
  /**
   * Сверить платёж со шлюзом и достроить оплату, если callback до бэкенда не доехал.
   * POST /api/v2/payments/alfabank/sync-status
   *
   * Банк повторяет callback всего 3 раза с интервалом 30 секунд — если в этот момент
   * бэкенд был недоступен, уведомление теряется навсегда, и заказ так и висит неоплаченным.
   *
   * @param paymentId mdOrder — идентификатор заказа в шлюзе (order.externalPaymentId)
   */
  async syncStatus(paymentId: string): Promise<AlfaBankSyncStatusResult> {
    logger.debug('Сверяем платёж со шлюзом Альфа-Банка', { paymentId })

    const response = await useApi(true).post('/payments/alfabank/sync-status', {
      body: { paymentId },
    })

    if (response.data?.recovered) {
      logger.warn('Оплата достроена по данным шлюза — callback банка был потерян', { paymentId })
    }

    return response.data
  }

  /**
   * Забрать параметры для нативной кнопки Alfa Pay (только мобильное приложение).
   * POST /api/v2/payments/alfabank/alfapay-config
   *
   * Заказ в шлюзе при этом НЕ регистрируется: это делает сама кнопка SDK по открытому
   * токену мерчанта. Поэтому `orderNumber` из ответа надо передать в кнопку без изменений —
   * по нему бэкенд свяжет callback банка с нашим заказом.
   */
  async getAlfaPayConfig(params: {
    orderId: number
    userId: string
    branchId: string
    isRetry?: boolean
  }): Promise<AlfaPayNativeConfigResult> {
    logger.debug('Запрашиваем конфигурацию нативной кнопки Alfa Pay', params)

    const response = await useApi(true).post('/payments/alfabank/alfapay-config', {
      body: params,
    })

    if (!response.data?.available) {
      logger.warn('Нативная кнопка Alfa Pay недоступна', { reason: response.data?.reason })
    }

    return response.data
  }
}

export const apiAlfaBank = new APIAlfaBank()
export default apiAlfaBank
